<template>
    <button
        class="stf-button"
        :class="cssClass"
        @click="$emit('click', $event)"
        v-ripple
        :disabled="loading"
    >
        <div v-if="loading" class="spinner"></div>
        <StfIcon v-else-if="icon" :icon="icon" color="white"/>
        <slot v-else>{{ label }}</slot>
    </button>
</template>

<script setup lang="ts">
import {computed} from 'vue';
import StfIcon from '@/ui/icon/StfIcon.vue';
import { type IconName } from '@/ui/icon/icons';

interface Props {
    size?: 'small' | 'medium' | 'large'
    label?: string
    color?: 'action' | 'primary' | 'default'
    icon?: IconName
    loading?: boolean
    variant?: 'plain' | 'outline' | 'text' | 'link'
    fillHeight?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    size: 'medium',
    color: 'action',
    label: '',
    loading: false,
    variant: 'plain',
    fillHeight: false,
})

const cssClass = computed(() => {
    const classes = [
        `stf-button--${props.size}`,
        `stf-button--${props.color}`,
        `stf-button--${props.variant}`
    ]

    if (props.fillHeight) {
        classes.push('stf-button--fill-height')
    }

    if (props.loading) {
        classes.push('stf-button--loading')
    }

    return classes.join(' ')
})

defineEmits<{
    click: [event: MouseEvent]
}>()
</script>
<style lang="scss">
@use "bulma/sass/utilities/mixins";
@use 'sass:color';
@use '@/assets/sass/_variables' as vars;

.stf-button {
  color: vars.$color-white;
  text-align: center;
  font-family: vars.$font-family-primary;
  font-size: vars.$font-size-17;
  font-weight: vars.$font-weight-bold;
  line-height: 160%;
  width: 100%;
  height: 100%;
  gap: 15px;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &--medium {
    font-size: vars.$font-size-17;
    height: 27px;
    @include mixins.mobile {
      font-size: vars.$font-size-14;
      height: 23px;
    }
  }

  &--large {
    font-size: vars.$font-size-25;
    font-weight: vars.$font-weight-semibold;
    height: 40px;
    @include mixins.mobile {
      font-size: vars.$font-size-20;
    }
  }

  &--fill-height {
    height: 100%;
  }

  &--small {
    font-size: vars.$font-size-14;
    height: 22px;
    @include mixins.mobile {
      font-size: vars.$font-size-12;
      height: 19px;
    }
  }

  &--loading {
    color: transparent;
  }

  &--plain {
    &.stf-button--action {
    background-color: vars.$color-action;
    &:hover:not(:disabled) {
      background-color: color.adjust(vars.$color-action, $lightness: -10%);
    }
  }

  &.stf-button--primary {
    background-color: vars.$color-primary;
    &:hover:not(:disabled) {
      background-color: color.adjust(vars.$color-primary, $lightness: -10%);
    }
  }
  }

  &--outline {
    background-color: transparent;
    border: 1px solid vars.$color-black;
    color: vars.$color-black;
    &.stf-button--action {
      color: vars.$color-action;
      border-color: vars.$color-action;
    }

    &.stf-button--primary {
      color: vars.$color-primary;
      border-color: vars.$color-primary;
    }

    &:hover:not(:disabled) {
        border-width: 2px;
    }
  }

  &--link {
    background-color: transparent;
    color: vars.$color-link;
    text-decoration: underline;
    font-weight: vars.$font-weight-normal;
  }

  &--text{
    background-color: transparent;
    color: vars.$color-black;
    &:hover:not(:disabled) {
      background-color: color.adjust(vars.$color-black, $lightness: 85%);
    }

    &.stf-button--action {
    color: vars.$color-action;
    &:hover:not(:disabled) {
      background-color: color.adjust(vars.$color-action, $lightness: 35%);
    }
  }

  &.stf-button--primary {
    color: vars.$color-primary;
    &:hover:not(:disabled) {
      background-color: color.adjust(vars.$color-primary, $lightness: 35%);
    }
  }
  }

  .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: #fff;
    animation: spin 0.8s linear infinite;
    position: absolute;
  }

  &--small .spinner {
    width: 16px;
    height: 16px;
    border-width: 2px;
  }

  &--large .spinner {
    width: 24px;
    height: 24px;
    border-width: 3px;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
